import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { QueryParameters } from '@deck.gl/carto'
import { ApiError, RequestStatus } from 'features/common'
import { Connection } from 'features/connections'
import { DataExplorerTable } from 'features/data-explorer/types'
import { MapType, SqlParameter } from 'features/maps/types'
import { Analysis, AnalysisType, SettingsSnapshot } from '../types'

const name = 'analysis'
const ANALYSIS_SQL_PANEL_REQUEST = `${name}/analysis_to_sql_panel_request`
const LAUNCH_ANALYS_REQUEST = `${name}/launch_analysis_request`
const CHECK_ANALYSES_REQUEST = `${name}/check_analyses_request`
const COLUMN_STATS_REQUEST = `${name}/fetch_column_stats`

export enum AnalysisTarget {
  Table = 'table',
  Sql = 'sql'
}

export enum SQLMode {
  Preview = 'preview',
  Execute = 'execute'
}

export interface SecoundSourceTable extends DataExplorerTable {
  source: string
  geoColumn: string
}

export interface QuerySource {
  query: string
  sqlParameters?: SqlParameter[]
  geoColumn: string
}

interface AnalysisState {
  selectedAnalysis?: AnalysisType
  connection?: Connection
  sourceDatasetId?: string
  querySource?: QuerySource
  destination?: string
  runningAnalyses: Analysis[]
  columnStatsStatus: RequestStatus
  columnStats?: { attribute: string; max: number; min: number } | null
  columnStatsError?: ApiError | null
  target: AnalysisTarget
  SQLMode?: SQLMode
  sourceFromDataTreeMode?: boolean
  secondSourceTable?: SecoundSourceTable | null
  settingsSnapshot?: SettingsSnapshot | null
}

const initialState: AnalysisState = {
  runningAnalyses: [],
  target: AnalysisTarget.Sql,
  SQLMode: SQLMode.Execute,
  columnStatsStatus: RequestStatus.Idle
}

const asyncActions = {
  launchAnalysisRequest: createAction(LAUNCH_ANALYS_REQUEST),
  analysisToSQLPanelRequest: createAction<boolean>(ANALYSIS_SQL_PANEL_REQUEST),
  checkAnalysesRequest: createAction<string>(CHECK_ANALYSES_REQUEST),
  fetchColumnStatsRequest: createAction<{
    connection: string
    source: string
    queryParameters?: QueryParameters | null
    column: string
    type: MapType
  }>(COLUMN_STATS_REQUEST)
}

const reducers = {
  setAnalysisConnection: (state: AnalysisState, action: PayloadAction<Connection>) => {
    state.connection = action.payload
  },
  setSourceDatasetId: (state: AnalysisState, action: PayloadAction<string>) => {
    state.sourceDatasetId = action.payload
  },
  setSourceFromDataTreeMode: (state: AnalysisState, action: PayloadAction<boolean>) => {
    state.sourceFromDataTreeMode = action.payload
  },
  setAnalysis: (state: AnalysisState, action: PayloadAction<Analysis>) => {
    const analysisIdx = state.runningAnalyses.findIndex((analysis) => analysis.id === action.payload.id)
    const runningAnalyses = [...state.runningAnalyses]
    runningAnalyses[analysisIdx] = action.payload
    state.runningAnalyses = runningAnalyses
  },
  addAnalysis: (state: AnalysisState, action: PayloadAction<Analysis>) => {
    state.runningAnalyses = [...state.runningAnalyses, action.payload]
  },
  setTarget: (state: AnalysisState, action: PayloadAction<AnalysisTarget>) => {
    state.target = action.payload
  },
  removeAnalysis: (state: AnalysisState, action: PayloadAction<string>) => {
    const runningAnalyses = state.runningAnalyses
    const indexToRemove = runningAnalyses.findIndex((analysis) => analysis.id === action.payload)
    runningAnalyses.splice(indexToRemove, 1)
    state.runningAnalyses = runningAnalyses
  },
  setSelectedAnalysis: (state: AnalysisState, action: PayloadAction<AnalysisType | undefined>) => {
    state.selectedAnalysis = action.payload
  },
  setQuerySource: (state: AnalysisState, action: PayloadAction<QuerySource | undefined>) => {
    state.querySource = action.payload
  },
  setDestination: (state: AnalysisState, action: PayloadAction<string | undefined>) => {
    state.destination = action.payload
  },
  setColumnStatsStatus: (state: AnalysisState, action: PayloadAction<RequestStatus>) => {
    state.columnStatsStatus = action.payload
  },
  setColumnStats: (
    state: AnalysisState,
    action: PayloadAction<{ attribute: string; max: number; min: number } | null>
  ) => {
    state.columnStats = action.payload
  },
  setColumnStatsError: (state: AnalysisState, action: PayloadAction<ApiError | null>) => {
    state.columnStatsError = action.payload
  },
  cleanUpAnalysis: (state: AnalysisState) => {
    state.selectedAnalysis = undefined
    state.querySource = undefined
    state.destination = undefined
    state.secondSourceTable = undefined
  },
  setSecondSourceTable: (state: AnalysisState, action: PayloadAction<SecoundSourceTable | null>) => {
    state.secondSourceTable = action.payload
  },
  setSqlMode(state: AnalysisState, action: PayloadAction<SQLMode>) {
    state.SQLMode = action.payload
  },
  setSettingsSnapshot: (state: AnalysisState, action: PayloadAction<SettingsSnapshot | null>) => {
    state.settingsSnapshot = action.payload
  }
}

export const analysisSlice = createSlice({
  name,
  initialState,
  reducers
})

export const {
  setAnalysisConnection,
  setSelectedAnalysis,
  setQuerySource,
  setDestination,
  setAnalysis,
  addAnalysis,
  removeAnalysis,
  setSourceDatasetId,
  setColumnStats,
  setColumnStatsError,
  setColumnStatsStatus,
  cleanUpAnalysis,
  setSourceFromDataTreeMode,
  setSecondSourceTable,
  setTarget,
  setSqlMode,
  setSettingsSnapshot
} = analysisSlice.actions
export const { launchAnalysisRequest, analysisToSQLPanelRequest, checkAnalysesRequest, fetchColumnStatsRequest } =
  asyncActions

export default analysisSlice.reducer
